<script setup lang="ts">
import type { DropdownMenuItem } from '@nuxt/ui'
import type { PaAPI } from '@rialtic/types'

const { $auth0 } = useNuxtApp()
const { getAllFlags, flags } = useAppConfiguration()

getAllFlags()

const { user } = $auth0()

const workspace = inject<ComputedRef<PaAPI.Get['/workspace/{id}']>>('workspace')
const workspaceUnderMaintenance = inject<ComputedRef<boolean>>(
  'workspace-under-maintenance',
)

const underMaintenance = computed(
  () =>
    Boolean(flags.value.maintenance) ||
    Boolean(workspaceUnderMaintenance?.value),
)

const isRialticUser = computed(() =>
  Boolean(user.value?.email?.endsWith('@rialtic.io')),
)

const onSignOut = useLogout()

const menuItems = computed<DropdownMenuItem[][]>(() => [
  [
    {
      label:
        user.value?.name !== user.value?.email ? user.value?.name : undefined,
      description: user.value?.email,
      type: 'label',
    },
  ],
  [{ label: 'Log out', onSelect: onSignOut }],
])
</script>

<template>
  <div class="grid h-dvh grid-rows-[64px_auto] bg-white">
    <header
      class="sticky top-0 z-30 h-16 w-full border-b border-neutral-200 bg-white"
      :class="{ 'bg-transparent': underMaintenance }"
    >
      <div
        class="flex items-center justify-between px-4 py-2 lg:container lg:mx-auto lg:px-6"
      >
        <NuxtLink
          to="/"
          class="text-h1 flex items-center space-x-2 rounded pr-4 hover:underline hover:opacity-90"
        >
          <FindClaimIcon class="h-10 w-11" />
          <span>Find a Claim</span>
        </NuxtLink>

        <div class="flex items-center space-x-4">
          <UBadge
            v-if="underMaintenance"
            class="border-warning-400 bg-warning-200 text-warning-800 rounded border-2 border-dashed px-2 py-1 text-xs ring-0"
            data-testid="maintenance-badge"
          >
            Under Maintenance
          </UBadge>
          <UDropdownMenu :items="menuItems">
            <UButton
              class="ring-default hover:ring-default"
              aria-label="User account menu"
              color="neutral"
              variant="outline"
              data-analytics="top-bar-account-dropdown"
            >
              <span class="flex flex-col items-start">
                <img
                  v-if="workspace?.logo"
                  class="hidden h-5 max-w-20 lg:block"
                  :src="workspace.logo"
                  alt="workspace logo"
                />
                <span
                  v-if="workspace?.name"
                  class="hidden max-w-40 truncate text-xs lg:inline"
                  data-testid="userMenuWorkspaceName"
                >
                  {{ workspace.name }}
                </span>
              </span>
              <UAvatar :src="user?.picture" icon="i-ph-user" size="md" />
            </UButton>
          </UDropdownMenu>
        </div>
      </div>
    </header>

    <slot />

    <div
      v-if="underMaintenance && !isRialticUser"
      class="fixed inset-0 z-10 grid h-screen w-full items-center justify-center backdrop-blur-sm"
      data-testid="maintenance-overlay"
    >
      <UCard
        class="border-warning-300 border-4 border-dashed text-center"
        :ui="{ root: 'bg-warning-100', body: 'px-16 py-10' }"
      >
        <h2 class="h3 text-warning-700 pb-2">Under maintenance</h2>

        <p class="body-1 text-neutral-900">
          This service is currently under maintenance.
        </p>
      </UCard>
    </div>
  </div>
</template>
